import { ErrorBoundary } from '@/components/Rollbar';
import { ExportFilesSearchRefetchProvider } from '@/context/services/export_files/refetchContext';
import { ExportFileColumn } from '@/features/export_files/List/type';
import { useTranslation } from '@/i18n';
import {
  ModernPaginationProps,
  Table,
  TableProps,
} from '@moneyforward/ap-frontend-components';
import { FC, Suspense, memo, useMemo } from 'react';
import { useExportFilesColumns } from './Columns/column';
import { useExportFileActions } from './hooks/useExportFileActions';
import { useExportFilesTablePagination } from './hooks/useExportFilesTablePagination';
import { useConfirmExportFileModal } from '@/features/export_files/List/Table/hooks/useConfirmExportFileModal';

export type ExportFilesTableProps = Omit<
  TableProps<ExportFileColumn>,
  'columns' | 'pagination' | 'emptyType'
>;

type Props = {
  pagination: ModernPaginationProps;
  table: ExportFilesTableProps;
};

type ErrorExportFilesTableProps = {
  error: Error | null;
};

export const ExportFilesTableError: FC<ErrorExportFilesTableProps> = memo(
  () => {
    const pagination = useMemo(
      () => ({
        currentPage: 1,
        maxPage: 1,
        perPage: 1,
        maxRecord: 0,
      }),
      []
    );

    const tableProps = useMemo(
      () => ({ data: [], loading: false, emptyType: 'error' }),
      []
    );
    return <ExportFilesTable table={tableProps} pagination={pagination} />;
  }
);
ExportFilesTableError.displayName = 'ExportFilesTableError';

export const ExportFilesTable: FC<Props> = ({ table, pagination }) => {
  const { t } = useTranslation();
  const { confirm, ConfirmModal } = useConfirmExportFileModal();
  const actionHandlers = useExportFileActions(confirm);
  const { ...handlers } = actionHandlers;
  const columns = useExportFilesColumns(handlers);

  return (
    <>
      <Table
        emptyType='file'
        {...table}
        legacy={false}
        key={pagination.currentPage}
        emptyText={t('export_files_list_empty_message')}
        columns={columns}
        pagination={pagination}
        rowKey='id'
        testId='export-files-table'
      />
      <ConfirmModal />
    </>
  );
};
ExportFilesTable.displayName = 'ExportFilesTable';

export const ExportFilesTableLoading: FC = memo(() => {
  const pagination = useMemo(
    () => ({
      currentPage: 1,
      maxPage: 1,
      perPage: 1,
      maxRecord: 0,
    }),
    []
  );

  const tableProps = useMemo(() => ({ data: [], loading: true }), []);
  return <ExportFilesTable table={tableProps} pagination={pagination} />;
});
ExportFilesTableLoading.displayName = 'ExportFilesTableLoading';

const InnerTable = memo(() => {
  const { table, pagination, refetch } = useExportFilesTablePagination();
  return (
    <ExportFilesSearchRefetchProvider value={refetch}>
      <ExportFilesTable table={table} pagination={pagination} />
    </ExportFilesSearchRefetchProvider>
  );
});
InnerTable.displayName = 'InnerTable';

export const ExportFilesTableView = memo((props) => {
  return (
    <ErrorBoundary fallback={ExportFilesTableError}>
      <Suspense fallback={<ExportFilesTableLoading />}>
        <InnerTable {...props} />
      </Suspense>
    </ErrorBoundary>
  );
});
ExportFilesTableView.displayName = 'ExportFilesTableView';
